import { Combobox, Transition } from "@headlessui/react";
import { CheckIcon } from "@heroicons/react/24/solid";
import { Fragment, useState } from "react";

type PickupLocation = {
  id: string;
  location: string;
  remarks: string;
};

const mockPickupLocations: PickupLocation[] = [
  {
    id: "1",
    location: "Main warehouse — Ring Road",
    remarks: "Mon–Sat 9am–6pm",
  },
  {
    id: "2",
    location: "East branch pickup desk",
    remarks: "Closed Sundays",
  },
  {
    id: "3",
    location: "Airport express counter",
    remarks: "24/7 locker access",
  },
];

const SelectPickupCard = () => {
  const [query, setQuery] = useState("");
  const [selectedPickupLocation, setSelectedPickupLocation] = useState<PickupLocation | null>(null);

  const filterLocation = (q: string) => {
    if (q === "") {
      return mockPickupLocations;
    }
    return mockPickupLocations.filter((d) => d.location.toLowerCase().includes(q.toLowerCase()));
  };

  const filtered = filterLocation(query);

  return (
    <div className="bg-gray-200 rounded-sm flex-1 p-2 my-2">
      <div className="pb-4">
        <Combobox value={selectedPickupLocation} onChange={setSelectedPickupLocation}>
          {({ open }) => (
            <div className="relative w-full mt-4">
              <Combobox.Button className="flex items-center justify-between w-full bg-white rounded-2xl p-4 shadow-sm border border-gray-100 cursor-pointer focus:outline-none">
                <div className="flex items-start space-x-3">
                  <div className="h-8 w-8 bg-green-100 flex items-center justify-center rounded-full mt-1">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                      strokeWidth={1.5}
                      stroke="currentColor"
                      className="w-5 h-5 text-green-700"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M3 3l1.664 1.664m0 0L12 12m-7.336-7.336L3 9m10.5 10.5l-1.664 1.664M13.5 19.5L21 12m-7.5 7.5l-3-3m3 3l-3-3"
                      />
                    </svg>
                  </div>

                  <div className="flex flex-col text-left min-w-0">
                    <p className="text-gray-800 text-sm font-medium">Pickup Zone</p>
                    <p className="text-gray-400 text-xs truncate">
                      {selectedPickupLocation
                        ? selectedPickupLocation.location
                        : "Please select the pickup zone closest to you"}
                    </p>
                  </div>
                </div>

                <span className="shrink-0 text-gray-700 text-sm font-medium bg-gray-100 px-3 py-1.5 rounded-lg">
                  Choose
                </span>
              </Combobox.Button>

              <div className="mt-3">
                <Combobox.Input
                  className="w-full rounded-2xl border border-gray-200 bg-white py-2 px-3 text-sm text-gray-900 shadow-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
                  onChange={(event) => setQuery(event.target.value)}
                  displayValue={(loc: PickupLocation | null) => loc?.location ?? ""}
                  placeholder="Search pickup zones"
                />
              </div>

              <Transition
                show={open}
                as={Fragment}
                leave="transition ease-in duration-100"
                leaveFrom="opacity-100"
                leaveTo="opacity-0"
              >
                <Combobox.Options className="absolute z-10 mt-2 w-full bg-white shadow-lg max-h-60 rounded-xl py-2 text-base ring-1 ring-black ring-opacity-5 overflow-auto focus:outline-none sm:text-sm">
                  {filtered.length > 0 ? (
                    filtered.map((d) => (
                      <Combobox.Option
                        key={d.id}
                        value={d}
                        className={({ active }) =>
                          `${
                            active ? "bg-green-100 text-green-900" : "text-gray-900"
                          } cursor-default select-none relative py-2 pl-3 pr-9`
                        }
                      >
                        {({ active, selected }) => (
                          <div className="flex flex-col">
                            <div className="flex justify-between items-center">
                              <span
                                className={`truncate text-sm ${selected ? "font-semibold" : "font-normal"}`}
                              >
                                {d.location}
                              </span>
                            </div>
                            <span
                              className={`truncate text-xs ${active ? "text-green-700" : "text-gray-500"}`}
                            >
                              {d.remarks}
                            </span>

                            {selected && (
                              <span
                                className={`absolute inset-y-0 right-0 flex items-center pr-4 ${
                                  active ? "text-green-700" : "text-green-600"
                                }`}
                              >
                                <CheckIcon className="h-5 w-5" aria-hidden="true" />
                              </span>
                            )}
                          </div>
                        )}
                      </Combobox.Option>
                    ))
                  ) : (
                    <div className="px-4 py-2 text-gray-500 text-sm">No pickup zones found</div>
                  )}
                </Combobox.Options>
              </Transition>

              {selectedPickupLocation && (
                <div className="flex justify-between items-center mt-2 px-1">
                  <span className="text-sm text-gray-500">{selectedPickupLocation.remarks}</span>
                </div>
              )}
            </div>
          )}
        </Combobox>
      </div>
    </div>
  );
};

export default SelectPickupCard;
